import * as Yup from "yup"
import { Gender, BookerInput, ThirdPartyUserInput } from "../../generated/graphql"
import { BaseInputValidationSchema } from "../common/BaseInputValidationSchema"
import { BookerAttendeeInputValidationSchema } from "../CreateBooking/Steps/validationSchemas/BookerAttendeeInputValidationSchema"
import { BookerPaymentMethodInputValidationSchema } from "../CreateBooking/Steps/validationSchemas/BookerPaymentMethodInputValidationSchema"
import { v4 } from "uuid"
import moment from "moment"
import { TFunction } from "i18next"

const { string, object, mixed, boolean, array, date } = Yup

export const buildBookerInputValidationSchema = (t: TFunction): Yup.AnyObjectSchema =>
    object().shape(
        {
            id: string().required(),
            // customer: BaseInputValidationSchema.required(),
            gender: mixed<Gender>()
                .oneOf(Object.values(Gender) as Gender[])
                .required(),
            firstname: string().min(3, "Der Vorname muss mindestens 3 Zeichen haben.").required("Vorname ist ein Pflichtfeld"),
            lastname: string().min(3, "Der Nachname muss mindestens 3 Zeichen haben.").required("Nachname ist ein Pflichtfeld"),
            street: string()
                .matches(/([a-zA-Z])+.+\d/, "Hausnummer vergessen?")
                .min(3, "Bitte gib eine Sinnvolle Straße ein")
                .required("Straße, Hausnummer ist ein Pflichtfeld"),
            houseNumber: string().notRequired().nullable(),
            postCode: string()
                .required("PLZ ist ein Pflichtfeld")
                .matches(/^([A-Z]-)?[0-9]+$/, "Nur Zahlen und optional ein Buchstabe mit Bindestrich (z.B. 'D-') sind erlaubt")
                .test("length", "Bitte gib eine gültige Postleitzahl ein", (value) => {
                    // Entferne "X-" Prefix falls vorhanden und prüfe dann die Länge
                    const numericPart = value?.replace(/^[A-Z]-/, "") || ""
                    return numericPart.length >= 4 && numericPart.length <= 5
                }),
            city: string().min(3, "Bitte gib einen sinnvollen Ort ein.").required("Ort ist ein Pflichtfeld"),
            phoneNumber: string()
                // .matches(/^[0-9]+$/, "Nur Zahlen sind erlaubt")
                .matches(/^[0-9 ]+$/, "Nur Zahlen sind erlaubt")
                .notRequired()
                .nullable(),
            mobileNumber: string()
                .transform((value) => value.replace(/\s+/g, "")) // Alle Leerzeichen entfernen
                .required("Handy Nr. ist ein Pflichtfeld")
                .test("starts-with", "Deine Handynummer muss mit '0' oder '+49' beginnen.", (value) => !value || value.startsWith("0") || value.startsWith("+49"))
                .test("minimum-length", "Deine Handynummer ist zu kurz. Beispiele: '01711234567' oder '+491711234567'", (value) => {
                    if (!value) return false
                    if (value.startsWith("+49")) {
                        return value.length >= 12
                    }
                    return value.length >= 11
                })
                .test("valid-regex", "Bitte gib eine gültige Handynummer ein. Beispiele: '01711234567' oder '+491711234567'", (value) => !value || /^(0\d{10,}|\+491\d{8,})$/.test(value)),
            email: string().email().required(),
            initialContact: string().notRequired().nullable(),
            dsgvo: boolean().oneOf([true], "Bitte akzeptiere die AGBs.").required(),
            newsletter: boolean().required(),
            healthInsuranceFund: string().notRequired().nullable(),
            annotation: string().notRequired().nullable(),
            paymentMethods: array().of(BookerPaymentMethodInputValidationSchema).required(),
            attendees: array().of(BookerAttendeeInputValidationSchema).notRequired().nullable(),
            birthday: date()
                .typeError("Bitte gib ein gültiges Geburtsdatum ein")
                .required("Geburtsdatum ist ein Pflichtfeld")
                .test("dateIsBeforeToday", "Das Geburtsdatum darf nicht in der Zukunft liegen", function (value) {
                    if (!value) return false

                    // Stelle sicher, dass value ein gültiges Datum ist
                    const dateValue = new Date(value)
                    if (isNaN(dateValue.getTime())) return false

                    return new Date().getTime() >= dateValue.getTime()
                })
                .test("underAge", t("registration.help.underAge", "Du musst als Bucher mindestens 18 Jahre alt sein"), function (value) {
                    if (!value) return false

                    // Stelle sicher, dass value ein gültiges Datum ist
                    const dateValue = new Date(value)
                    if (isNaN(dateValue.getTime())) return false

                    const ageInYears = moment().diff(dateValue, "years")
                    return ageInYears >= 18
                }),
        },
        [
            ["phoneNumber", "mobileNumber"],
            ["phoneNumber", "email"],
            ["mobileNumber", "email"],
        ]
    )

export const bookerInputInitialValues: BookerInput = {
    id: v4(),
    customer: { id: v4() },
    gender: Gender.Female,
    firstname: "",
    lastname: "",
    street: "",
    houseNumber: "",
    postCode: "",
    city: "",
    email: "",
    annotation: undefined,
    // eslint-disable-next-line no-warning-comments
    // birthday: new Date(0).toISOString(), // TODO:  Handle birthday input, this is temporary to prevent error
    birthday: undefined, // TODO:  Handle birthday input, this is temporary to prevent error
    initialContact: new Date().toISOString(),
    healthInsuranceFund: undefined,
    dsgvo: false,
    newsletter: false,
    mobileNumber: "",
    phoneNumber: "",
    attendees: [],
    paymentMethods: [],
}

export const buildThirdPartyUserValidationSchema = (t: TFunction): Yup.AnyObjectSchema =>
    object().shape({
        username: string().required(),
        email: string().required(),
        password: Yup.string().min(8, t("registration.help.passwordLength", "Passwort muss mindestens 8 Zeichen lang sein")).required(t("registration.help.password", "Passwort ist ein Pflichtfeld")),
        passwordConfirmation: Yup.string()
            .required(t("registration.help.confirmPassword", "Bitte wiederhole das Passwort"))
            .oneOf([Yup.ref("password"), null], t("registration.help.confirmPasswordMatch", "Die Passwörter müssen überinstimmen")),
    })

export const thirdPartyUserInitialValues: ThirdPartyUserInput & { passwordConfirmation: string } = {
    username: "",
    password: "",
    passwordConfirmation: "",
    email: "",
}

export type RegistrationPanelValidationType = ThirdPartyUserInput & BookerInput & { passwordConfirmation: string }

export const buildRegistrationPanelValidationSchema = (t: TFunction, useCase: "registration" | "account" = "registration"): Yup.AnyObjectSchema =>
    object().shape(
        {
            username:
                useCase === "registration"
                    ? string().email("Bitte gib eine gültige Emailadresse ein").required("Benutzername ist ein Pflichtfeld")
                    : string().email("Bitte gib eine gültige Emailadresse ein").notRequired().nullable(),
            // email: string().required().email("Bitte gib eine gültige E-Mail Adresse ein"),
            password:
                useCase === "registration"
                    ? Yup.string().min(8, "Passwort muss mindestens 8 Zeichen lang sein").required("Passwort ist ein Pflichtfeld")
                    : Yup.string().min(8, "Passwort muss mindestens 8 Zeichen lang sein").notRequired().nullable(),
            passwordConfirmation:
                useCase === "registration"
                    ? Yup.string()
                          .required(t("registration.help.confirmPassword", "Bitte wiederhole das Passwort"))
                          .oneOf([Yup.ref("password"), null], t("registration.help.confirmPasswordMatch", "Die Passwörter müssen überinstimmen"))
                    : Yup.string()
                          .notRequired()
                          .nullable()
                          .oneOf([Yup.ref("password"), null], t("registration.help.confirmPasswordMatch", "Die Passwörter müssen überinstimmen")),
            id: string().required(),
            customer: BaseInputValidationSchema.required(),
            gender: mixed<Gender>()
                .oneOf(Object.values(Gender) as Gender[])
                .required("Anrede ist ein Pflichtfeld"),
            firstname: string()
                .min(3, t("registration.contactDataForm.firstName.help.length", "Der Vorname muss mindestens 3 Zeichen haben."))
                .required(t("registration.contactDataForm.firstName.help.required", "Vorname ist ein Pflichtfeld")),
            lastname: string()
                .min(3, t("registration.contactDataForm.lastName.help.length", "Der Nachname muss mindestens 3 Zeichen haben."))
                .required(t("registration.contactDataForm.lastName.help.required", "Nachname ist ein Pflichtfeld")),
            street: string()
                .matches(/([a-zA-Z])+.+\d/, t("registration.contactDataForm.streetAndNumber.help.matches", "Hausnummer vergessen?"))
                .min(3, t("registration.contactDataForm.streetAndNumber.help.length", "Bitte gib eine Sinnvolle Straße ein"))
                .required(t("registration.contactDataForm.streetAndNumber.help.required", "Straße, Hausnummer ist ein Pflichtfeld")),
            houseNumber: string().notRequired().nullable(),
            // postCode: string().required("PLZ ist ein Pflichtfeld"),
            postCode: string()
                .required(t("registration.contactDataForm.zipCode.help.required", "PLZ ist ein Pflichtfeld"))
                .matches(/^([A-Z]-)?[0-9]+$/, t("registration.contactDataForm.zipCode.help.matches", "Nur Zahlen und optional ein Buchstabe mit Bindestrich (z.B. 'D-') sind erlaubt"))
                .test("length", t("registration.contactDataForm.zipCode.help.length", "Bitte gib eine gültige Postleitzahl ein"), (value) => {
                    // Entferne "X-" Prefix falls vorhanden und prüfe dann die Länge
                    const numericPart = value?.replace(/^[A-Z]-/, "") || ""
                    return numericPart.length >= 4 && numericPart.length <= 5
                }),
            city: string()
                .min(3, t("registration.contactDataForm.city.help.length", "Bitte gib einen sinnvollen Ort ein."))
                .required(t("registration.contactDataForm.city.help.required", "Ort ist ein Pflichtfeld")),
            phoneNumber: string()
                // .matches(/^[0-9]+$/, "Nur Zahlen sind erlaubt")
                .matches(/^[0-9 ]+$/, t("registration.contactDataForm.phoneNumber.help.matches", "Nur Zahlen sind erlaubt"))
                .notRequired()
                .nullable(),
            mobileNumber: string()
                .matches(/^(([0-9]{10,})+|(\+[0-9]{9,})+)$/, t("registration.contactDataForm.mobilePhone.help.matches", "Bitte gib eine gültige Handynummer ein"))
                .required(t("registration.contactDataForm.mobilePhone.help.required", "Handy Nr. ist ein Pflichtfeld")),
            // email: string().email().required(),
            initialContact: string().notRequired().nullable(),
            dsgvo: boolean().oneOf([true], t("registration.contactDataForm.agb.help", "Bitte akzeptiere die AGBs.")).required(),
            newsletter: boolean().required(),
            healthInsuranceFund: string().notRequired().nullable(),
            annotation: string().notRequired().nullable(),
            // paymentMethods: array().of(BookerPaymentMethodInputValidationSchema).required(),
            paymentMethods: array().of(BookerPaymentMethodInputValidationSchema).notRequired().nullable(),
            attendees: array().of(BookerAttendeeInputValidationSchema).notRequired().nullable(),
            birthday: date()
                .typeError(t("registration.help.dateIsInvalid", "Bitte gib ein gültiges Datum ein"))
                .required(t("registration.help.dateIsRequired", "Das Geburtsdatum ist ein Pflichtfeld"))
                .test("dateIsBeforeToday", t("registration.help.dateIsBeforeToday", "Das Geburtsdatum darf nicht in der Zukunft liegen"), async function (value) {
                    if (!value) return false

                    // if date is in the future
                    if (new Date().getTime() >= new Date(value).getTime()) {
                        return true
                    }

                    return false
                })
                .test("underAge", t("registration.help.underAge", "Du musst als Bucher mindestens 18 Jahre alt sein"), async function (value) {
                    if (!value) return false

                    const birthday = new Date(value).getTime()
                    const ageInYears = moment().diff(birthday, "years")

                    if (ageInYears >= 18) {
                        return true
                    }

                    console.log("FALSE")

                    return false
                }),
        },
        [
            ["phoneNumber", "mobileNumber"],
            ["phoneNumber", "email"],
            ["mobileNumber", "email"],
        ]
    )

export const registrationPanelValidationInitialValues: RegistrationPanelValidationType = {
    username: "",
    password: "",
    passwordConfirmation: "",
    email: "",
    id: v4(),
    customer: { id: v4() },
    gender: Gender.NotDefined,
    firstname: "",
    lastname: "",
    street: "",
    houseNumber: "",
    postCode: "",
    city: "",
    annotation: undefined,
    birthday: undefined, // TODO:  Handle birthday input, this is temporary to prevent error
    initialContact: new Date().toISOString(),
    healthInsuranceFund: undefined,
    dsgvo: false,
    newsletter: false,
    mobileNumber: "",
    phoneNumber: "",
    attendees: [],
    paymentMethods: [],
}
